import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { selectUserInfo } from '@reducers/user';
import { generateImageURL } from '@reducers/blobStorage';
import { uploadImageDocument } from '@reducers/v2/imageHandlingV2/thunks';
import { KEYS_TAGS, TAG_NAMING, DOC_TYPE } from '@components/imageHandling/common';
import type { ImageDocumentReference } from '@v2/components/imageHandling/types/types.d';
import type { OnAttachItemPressLinkParam } from '@components/common/AttachEntitiesList/types';
import type { OperationSearchItem } from '@interfaces/search';
import { plainTextToRtf, rtfToPlainText } from '@v2/components/FinishJobModal/helpers';

export type NoteLink = {
  scopeId: number;
  scopeReferenceId: number;
  scopeReferenceName?: string;
  isFavorite?: boolean;
  isSelected?: boolean;
};

interface UseManageFinishJobNoteParams {
  item: OperationSearchItem;
}

interface DraftData {
  mode: 'simple' | 'rich';
  plain: string;
  rich: string;
}

const DRAFT_STORAGE_KEY_PREFIX = 'finish-job-note-draft-';
const AUTOSAVE_DEBOUNCE_MS = 5000;

const useManageFinishJobNote = ({ item }: UseManageFinishJobNoteParams) => {
  const dispatch = useAppDispatch();
  const userInfo = useSelector(selectUserInfo);

  const [editorMode, setEditorMode] = useState<'simple' | 'rich'>('simple');
  const [isRteModalOpen, setIsRteModalOpen] = useState(false);
  const [plainNoteContent, setPlainNoteContent] = useState('');
  const [richNoteContent, setRichNoteContent] = useState('');
  const [touched, setTouched] = useState(false);
  const [noteLinks, setNoteLinks] = useState<NoteLink[]>([]);

  const richSnapshotRef = useRef('');
  const [rteKey, setRteKey] = useState(0);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftKey = `${DRAFT_STORAGE_KEY_PREFIX}${item.productionOperationId}`;

  // --- Build imageDocumentReferences from OperationSearchItem ---
  const imageDocumentReferences: ImageDocumentReference[] = [];

  if (item.partId) {
    imageDocumentReferences.push({
      scopeId: KEYS_TAGS.Part,
      scopeName: TAG_NAMING.Part,
      scopeReferenceId: item.partId,
      scopeReferenceName: item.partNumber ?? '',
    });
  }

  imageDocumentReferences.push({
    scopeId: KEYS_TAGS.ProductionOrder,
    scopeName: TAG_NAMING.ProductionOrder,
    scopeReferenceId: item.productionOrderId,
    scopeReferenceName: item.productionOrderNumber,
  });

  imageDocumentReferences.push({
    scopeId: KEYS_TAGS.Operation,
    scopeName: TAG_NAMING.Operation,
    scopeReferenceId: item.productionOperationId,
    scopeReferenceName: item.productionOperationNumber,
  });

  const imageContexts = [{ scopeId: KEYS_TAGS.Operation, scopeReferenceId: item.productionOperationId }];

  // --- Initialize noteLinks with all references pre-selected ---
  useEffect(() => {
    const initialLinks: NoteLink[] = imageDocumentReferences.map((ref) => ({
      scopeId: ref.scopeId,
      scopeReferenceId: ref.scopeReferenceId,
      scopeReferenceName: ref.scopeReferenceName,
      isFavorite: false,
      isSelected: true,
    }));
    setNoteLinks(initialLinks);
  }, [item.productionOperationId]);

  // --- Restore draft on mount ---
  useEffect(() => {
    try {
      const stored = localStorage.getItem(draftKey);
      if (stored) {
        const parsed: DraftData = JSON.parse(stored);
        setEditorMode(parsed.mode);
        setPlainNoteContent(parsed.plain);
        setRichNoteContent(parsed.rich);
        setTouched(parsed.mode === 'simple' ? parsed.plain.length > 0 : parsed.rich.length > 0);
      }
    } catch {
      // localStorage unavailable or invalid JSON — try as legacy bare string
      try {
        const stored = localStorage.getItem(draftKey);
        if (stored) {
          setRichNoteContent(stored);
          setEditorMode('rich');
          setTouched(true);
        }
      } catch {
        // localStorage unavailable
      }
    }
  }, [draftKey]);

  // --- Autosave draft (debounced) ---
  useEffect(() => {
    if (!touched) return;

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = setTimeout(() => {
      try {
        const draft: DraftData = { mode: editorMode, plain: plainNoteContent, rich: richNoteContent };
        localStorage.setItem(draftKey, JSON.stringify(draft));
      } catch {
        // localStorage unavailable
      }
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [plainNoteContent, richNoteContent, editorMode, touched, draftKey]);

  const clearDraft = useCallback(() => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    try {
      localStorage.removeItem(draftKey);
    } catch {
      // localStorage unavailable
    }
  }, [draftKey]);

  // --- RTE modal handlers ---
  const openRteModal = useCallback(() => {
    richSnapshotRef.current = richNoteContent;
    // Seed RTE with plain text content when switching from simple mode
    if (editorMode === 'simple' && plainNoteContent && !richNoteContent) {
      setRichNoteContent(plainTextToRtf(plainNoteContent));
      setRteKey((prev) => prev + 1);
    }
    setIsRteModalOpen(true);
  }, [richNoteContent, editorMode, plainNoteContent]);

  const handleRteDone = useCallback(() => {
    setEditorMode('rich');
    setIsRteModalOpen(false);
    setTouched(richNoteContent.length > 0);
  }, [richNoteContent]);

  const handleRteCancel = useCallback(() => {
    setRichNoteContent(richSnapshotRef.current);
    // Force Slate remount so it picks up the restored snapshot content
    setRteKey((prev) => prev + 1);
    // If was in simple mode before opening, stay in simple mode and restore plain text from RTF snapshot
    if (editorMode === 'simple') {
      const snapshotText = rtfToPlainText(richSnapshotRef.current);
      if (snapshotText) {
        setPlainNoteContent(snapshotText);
      }
    }
    setIsRteModalOpen(false);
  }, [editorMode]);

  // --- Plain note handler ---
  const handlePlainNoteChange = useCallback((text: string) => {
    setPlainNoteContent(text);
    setTouched(text.length > 0);
  }, []);

  // --- Toggle attachment links ---
  const onAttachItemPress = useCallback(({ link }: { link: OnAttachItemPressLinkParam; setLoadingState: (state: any) => void }) => {
    setNoteLinks((prev) => {
      const existingIdx = prev.findIndex((l) => l.scopeId === link.scopeId && l.scopeReferenceId === link.scopeReferenceId);

      if (existingIdx === -1) {
        return prev.concat({
          scopeId: link.scopeId,
          scopeReferenceId: link.scopeReferenceId,
          scopeReferenceName: link.scopeReferenceName,
          isSelected: true,
        });
      } else {
        return prev.filter((_, idx) => idx !== existingIdx);
      }
    });
  }, []);

  // --- Generate image URL (for editor blob fetch) ---
  const handleGenerateImageURL = useCallback(
    async (data: any) => dispatch(generateImageURL(data)),
    [dispatch]
  );

  // --- Derived values ---
  const hasNote = editorMode === 'simple' ? plainNoteContent.length > 0 : richNoteContent.length > 0;
  const noteDetailsText = editorMode === 'simple' ? plainNoteContent : '';

  // --- Upload note as RTF document (rich mode only) ---
  const uploadNote = useCallback(async () => {
    const userName = userInfo?.userProfile
      ? `${userInfo.userProfile.firstName} ${userInfo.userProfile.lastName}`
      : 'Unknown';

    const fileName = `Note ${moment().format('YYYY-MM-DD [at] HH:mm:ss')} by ${userName}.rtf`;
    const file = new File([richNoteContent], fileName, { type: 'application/rtf' });
    const tempID = `TEMPIMAGEID-${fileName}`;

    const tags = noteLinks
      .filter((link) => link.isSelected !== false)
      .map((link) => ({
        scopeId: link.scopeId,
        scopeReferenceId: link.scopeReferenceId,
        isFavorite: true,
      }));

    await dispatch(
      uploadImageDocument({
        imageContexts,
        documentType: DOC_TYPE.NOTE,
        photo: file,
        tags,
        rawContent: richNoteContent,
        name: fileName,
        tempID,
      })
    );

    clearDraft();
  }, [richNoteContent, noteLinks, imageContexts, userInfo, dispatch, clearDraft]);

  return {
    // NoteSection UI
    editorMode,
    isRteModalOpen,
    rteKey,
    plainNoteContent,
    richNoteContent,
    handlePlainNoteChange,
    setRichNoteContent,
    setTouched,
    openRteModal,
    handleRteDone,
    handleRteCancel,
    // Entity chips (for RTE modal)
    noteLinks,
    imageDocumentReferences,
    onAttachItemPress,
    handleGenerateImageURL,
    // Parent modal hook
    hasNote,
    noteDetailsText,
    uploadNote,
    touched,
    clearDraft,
  };
};

export default useManageFinishJobNote;
